const BUTTON_ICON_CLASS = 'has-venti-button-icon';
const BUTTON_ICON_SELECTOR = '.venti-button-icon';
const BUTTON_ICON_HOVER_CLASS = 'has-venti-button-hover-animation';
let buttonIconSvgInstanceCount = 0;

const replaceSvgUrlReferences = (value, idMap) => value.replace(
    /url\((\s*(['"]?))#([^)\s'"]+)\2(\s*)\)/g,
    (match, leading, quote, id, trailing) => (idMap.has(id)
        ? `url(${leading}#${idMap.get(id)}${trailing})`
        : match),
);

const makeSvgIdsUnique = (svgContainer) => {
    const idElements = Array.from(svgContainer.querySelectorAll('[id]'));

    if (!idElements.length) {
        return;
    }

    let ids;

    do {
        const instanceId = buttonIconSvgInstanceCount;

        buttonIconSvgInstanceCount += 1;
        ids = idElements.map((element, index) => `venti-button-icon-svg-${instanceId}-${index}`);
    } while (ids.some((id) => document.getElementById(id)));

    const idMap = new Map();

    idElements.forEach((element, index) => {
        const id = element.getAttribute('id');

        if (!idMap.has(id)) {
            idMap.set(id, ids[index]);
        }

        element.setAttribute('id', ids[index]);
    });

    svgContainer.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            let value = attribute.value;

            if ((attribute.name === 'href' || attribute.name === 'xlink:href') && value.startsWith('#')) {
                const id = value.slice(1);

                if (idMap.has(id)) {
                    value = `#${idMap.get(id)}`;
                }
            }

            value = replaceSvgUrlReferences(value, idMap);

            if (value !== attribute.value) {
                if (attribute.namespaceURI) {
                    element.setAttributeNS(attribute.namespaceURI, attribute.name, value);
                    return;
                }

                element.setAttribute(attribute.name, value);
            }
        });
    });

    svgContainer.querySelectorAll('style').forEach((styleElement) => {
        const value = replaceSvgUrlReferences(styleElement.textContent, idMap);

        if (value !== styleElement.textContent) {
            styleElement.textContent = value;
        }
    });
};

export const normalizeClassName = (className = '') => className
    .split(' ')
    .map((item) => item.trim())
    .filter(Boolean);

export const toggleClassName = (className, token, enabled) => {
    const classNames = normalizeClassName(className).filter((item) => item !== token);

    if (enabled) {
        classNames.push(token);
    }

    return classNames.join(' ');
};

export const getPositionClassName = (position) => `${BUTTON_ICON_CLASS}--${position}`;
export const getHoverAnimationClassName = (animation) => `${BUTTON_ICON_HOVER_CLASS}--${animation}`;

export const getButtonIconConfig = (attributes = {}) => ({
    url: attributes.ventiButtonImageUrl || '',
    alt: attributes.ventiButtonImageAlt || '',
    mime: attributes.ventiButtonImageMime || '',
    svg: attributes.ventiButtonImageSvg || '',
    position: attributes.ventiButtonImagePosition === 'after' ? 'after' : 'before',
    hoverAnimation: attributes.ventiButtonHoverAnimation || 'none',
});

export const createButtonIconElement = ({ alt, mime, svg, url, position }) => {
    const iconElement = document.createElement('span');

    iconElement.className = `venti-button-icon venti-button-icon--${position}`;

    if (!alt) {
        iconElement.setAttribute('aria-hidden', 'true');
    }

    if (mime === 'image/svg+xml' && svg) {
        const svgElement = document.createElement('span');

        svgElement.className = 'venti-button-icon__svg';
        svgElement.innerHTML = svg;
        makeSvgIdsUnique(svgElement);
        iconElement.appendChild(svgElement);

        return iconElement;
    }

    const imageElement = document.createElement('img');

    imageElement.src = url;
    imageElement.alt = alt;
    imageElement.loading = 'lazy';
    iconElement.appendChild(imageElement);

    return iconElement;
};

export const applyButtonIconToLink = (buttonLink, attributes = {}) => {
    if (!buttonLink) {
        return;
    }

    const config = getButtonIconConfig(attributes);
    const positionClassNames = normalizeClassName(buttonLink.className)
        .filter((className) => !className.startsWith(`${BUTTON_ICON_CLASS}--`))
        .filter((className) => !className.startsWith(`${BUTTON_ICON_HOVER_CLASS}--`))
        .filter((className) => className !== BUTTON_ICON_HOVER_CLASS);

    buttonLink.querySelectorAll(`:scope > ${BUTTON_ICON_SELECTOR}`).forEach((iconElement) => iconElement.remove());

    if (!config.url) {
        buttonLink.className = toggleClassName(positionClassNames.join(' '), BUTTON_ICON_CLASS, false);
        return;
    }

    const iconElement = createButtonIconElement(config);
    const classNameWithIcon = toggleClassName(positionClassNames.join(' '), BUTTON_ICON_CLASS, true);
    const classNameWithAnimation = toggleClassName(classNameWithIcon, BUTTON_ICON_HOVER_CLASS, config.hoverAnimation !== 'none');

    buttonLink.className = `${classNameWithAnimation} ${getPositionClassName(config.position)} ${config.hoverAnimation !== 'none' ? getHoverAnimationClassName(config.hoverAnimation) : ''}`.trim();

    if (config.position === 'after') {
        buttonLink.appendChild(iconElement);
        return;
    }

    buttonLink.insertBefore(iconElement, buttonLink.firstChild);
};

export const decorateButtonIcons = (scope = document) => {
    scope.querySelectorAll('.wp-block-button__link[data-venti-button-image-url]').forEach((buttonLink) => {
        applyButtonIconToLink(buttonLink, {
            ventiButtonImageUrl: buttonLink.dataset.ventiButtonImageUrl,
            ventiButtonImageAlt: buttonLink.dataset.ventiButtonImageAlt,
            ventiButtonImageMime: buttonLink.dataset.ventiButtonImageMime,
            ventiButtonImageSvg: buttonLink.dataset.ventiButtonImageSvg,
            ventiButtonHoverAnimation: buttonLink.dataset.ventiButtonHoverAnimation,
            ventiButtonImagePosition: buttonLink.dataset.ventiButtonImagePosition,
        });
    });
};

export function initializeButtonIconFrontend(scope = document) {
    decorateButtonIcons(scope);
}
